// Display labels for stored codes and machine-written English (audit actions, agent log lines, system
// reasons). Framework-free so node-web, cloud-admin and cloud-portal share one implementation.
import { catalogs, has, t } from './core.js';
/** Label for a stored code under a catalog namespace; unknown codes show as themselves (never a dotted key). */
export function codeLabel(locale, ns, value) {
    if (!value)
        return '—';
    const key = `${ns}.${value}`;
    return has(locale, key) ? t(locale, key) : value;
}
/** "stay.checked_in" → "الإقامة: تسجيل الدخول"; full overrides first, then entity + verb, else the code. */
export function auditActionLabel(locale, action) {
    // override keys contain dots ("auth.login"), so read the map directly rather than by key path
    const override = catalogs[locale].codes.auditAction[action];
    if (override)
        return override;
    const dot = action.lastIndexOf('.');
    if (dot < 0)
        return codeLabel(locale, 'codes.auditEntity', action);
    const entity = action.slice(0, dot).split('.')[0];
    const verb = action.slice(dot + 1);
    const e = has(locale, `codes.auditEntity.${entity}`);
    const v = has(locale, `codes.auditVerb.${verb}`);
    if (!e || !v)
        return action;
    return `${t(locale, `codes.auditEntity.${entity}`)}: ${t(locale, `codes.auditVerb.${verb}`)}`;
}
const REASONS = [
    [/^inspection passed$/i, 'codes.reasonText.inspectionPassed'],
    [/^night audit: occupied room needs service$/i, 'codes.reasonText.occupiedNeedsService'],
    [/^night audit (PC-\d+) override$/i, 'codes.reasonText.overrideCheck', (m) => ({ check: m[1] })],
    [
        /^night audit (\d{4}-\d{2}-\d{2})$/i,
        'codes.reasonText.nightAuditDate',
        (m) => ({ date: m[1] }),
    ],
    [/^ticket \S+ resolved$/i, 'codes.reasonText.ticketResolved'],
    [/^ticket \S+: (.*)$/i, 'codes.reasonText.ticket', (m) => ({ text: m[1] })],
    [/^maintenance \w+: (.*)$/i, 'codes.reasonText.maintenance', (m) => ({ text: m[1] })],
    [/^transfer$/i, 'codes.reasonText.transfer'],
    [/^mistake$/i, 'codes.reasonText.mistake'],
    [/^non-payment$/i, 'codes.reasonText.nonPayment'],
    [/^unknown booking$/i, 'codes.reasonText.unknownBooking'],
    [/^no reservation for this booking$/i, 'codes.reasonText.noReservation'],
    [/^expired$/i, 'codes.reasonText.expired'],
    [/^ended_by_hotel$/i, 'codes.reasonText.endedByHotel'],
    [/^registration$/i, 'codes.reasonText.registration'],
    [/^initial setup$/i, 'codes.reasonText.initialSetup'],
    [/^guest request$/i, 'codes.reasonText.guestRequest'],
];
/** System-written reasons (English in the database) → current locale; free text typed by staff passes through. */
export function reasonLabel(locale, reason) {
    if (!reason)
        return '';
    if (has(locale, `reasons.${reason}`))
        return t(locale, `reasons.${reason}`);
    for (const [re, key, vars] of REASONS) {
        const m = reason.match(re);
        if (m)
            return t(locale, key, vars?.(m));
    }
    return reason;
}
const AGENT = [
    [/^backup completed/i, 'health.msg.backupCompleted'],
    [/^backup failed/i, 'health.msg.backupFailed'],
    [/^restore drill passed/i, 'health.msg.drillPassed'],
    [/^restore drill failed/i, 'health.msg.drillFailed'],
    [/^resumed after unclean shutdown/i, 'health.msg.resumedUnclean'],
    [/^node activated with the cloud/i, 'health.msg.nodeActivated'],
    [/^license applied from the cloud/i, 'health.msg.licenseApplied'],
    [/^license revoked by the cloud/i, 'health.msg.licenseRevoked'],
    [/^channel booking → (\S+)/i, 'health.msg.channelBooking', (m) => ({ ref: m[1] })],
    [/^updated to (\S+)/i, 'health.msg.updated', (m) => ({ v: m[1] })],
    [/^agent stopping/i, 'health.msg.agentStopping'],
    [/^update to (\S+) rolled back/i, 'health.msg.updateRolledBack', (m) => ({ v: m[1] })],
    [/^update to (\S+) failed before switching/i, 'health.msg.updateFailed', (m) => ({ v: m[1] })],
    [/^update manifest rejected/i, 'health.msg.manifestRejected'],
    [/^support snapshot failed/i, 'health.msg.supportSnapshotFailed'],
    [
        /^Syrova support requests (\S+) access \(ticket ([^)]+)\)/i,
        'health.msg.supportRequest',
        (m) => ({ scope: m[1], ticket: m[2] }),
    ],
    [/^command (\S+) rejected/i, 'health.msg.commandRejected', (m) => ({ cmd: m[1] })],
    [/^wall clock is behind/i, 'health.msg.clockBehind'],
    [/^disk critical/i, 'health.msg.diskCritical'],
    [/^replay import/i, 'health.msg.replayImport'],
    [/^CLOUD_PUBLIC_KEYS is empty/i, 'health.msg.cloudKeysMissing'],
];
/** node-agent writes its log lines in English; show them in the UI language when we know the line. */
export function agentMessageLabel(locale, message) {
    for (const [re, key, vars] of AGENT) {
        const m = message.match(re);
        if (m)
            return t(locale, key, vars?.(m));
    }
    return message;
}
//# sourceMappingURL=labels.js.map